import { Dialog, Fade, Stack, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { forwardRef } from "react";

import { sanitizeHtml } from "../lib/sanitizeHtml";
import { type GuideButtonContent } from "../types";
import { GuideMedia } from "./GuideMedia";
import { type BannerContent } from "./KnockGuidesBannerView";

export interface KnockGuidesModalContent extends BannerContent {
  image?: {
    url: string;
    alt?: string;
  };
}

export interface KnockGuidesModalViewProps {
  content: KnockGuidesModalContent;
  onPrimaryClick?: (button: GuideButtonContent) => void | Promise<void>;
  onSecondaryClick?: (button: GuideButtonContent) => void | Promise<void>;
  onDismiss?: () => void;
  /**
   * Whether the modal is visible.
   * @default true
   */
  open?: boolean;
  sx?: SxProps<Theme>;
}

const DIALOG_TIMING = { enter: 300, exit: 250 };

const FadeTransition = forwardRef<HTMLDivElement, { children: React.ReactElement; in?: boolean }>(
  (props, ref) => <Fade ref={ref} {...props} timeout={DIALOG_TIMING} />
);

FadeTransition.displayName = "FadeTransition";

export function KnockGuidesModalView(props: KnockGuidesModalViewProps) {
  const { content, onPrimaryClick, onSecondaryClick, onDismiss, open = true, sx } = props;
  const { title, body, primaryButton, secondaryButton, dismissible, image } = content;

  const showDismiss = dismissible && onDismiss;
  const hasActions = Boolean(primaryButton ?? secondaryButton);

  return (
    <Dialog
      open={open}
      TransitionComponent={FadeTransition}
      sx={mergeSxProps(
        (theme: Theme) => ({
          "& .MuiDialog-paper": {
            width: 480,
            maxWidth: "calc(100% - 32px)",
            margin: theme.spacing(4),
            borderRadius: 1,
            boxShadow: theme.shadow?.modal,
            backgroundColor: theme.palette.background?.standout,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        }),
        sx
      )}
      onClose={onDismiss}
    >
      {showDismiss && (
        <IconButton
          aria-label="Dismiss guide"
          iconType="close"
          size="small"
          variant="muted"
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(2),
            right: theme.spacing(2),
            zIndex: 1,
          })}
          onClick={onDismiss}
        />
      )}

      <Stack
        spacing={2}
        sx={(theme) => ({
          padding: theme.spacing(5),
          paddingBottom: image?.url || hasActions ? 0 : theme.spacing(5),
          textAlign: "left",
        })}
      >
        <Text
          semibold
          variant="h5"
          sx={(theme) => ({
            color: theme.palette.base?.strong ?? theme.palette.text.primary,
            wordBreak: "break-word",
            textWrap: "balance",
          })}
        >
          {title}
        </Text>

        <Text
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.base?.base ?? theme.palette.text.secondary,
            wordBreak: "break-word",
          })}
          dangerouslySetInnerHTML={sanitizeHtml({ potentiallyDangerousHtml: body })}
        />
      </Stack>

      {image?.url && (
        <GuideMedia
          alt={image.alt ?? ""}
          url={image.url}
          sx={(theme) => ({
            marginX: theme.spacing(5),
            marginTop: theme.spacing(3),
            marginBottom: "20px",
          })}
        />
      )}

      {hasActions && (
        <Stack
          spacing={3}
          sx={(theme) => ({
            paddingX: theme.spacing(5),
            paddingBottom: theme.spacing(5),
            paddingTop: image?.url ? 0 : theme.spacing(3),
          })}
        >
          {primaryButton && (
            <Button
              fullWidth
              variant="primary"
              onClick={async () => {
                await onPrimaryClick?.(primaryButton);
              }}
            >
              {primaryButton.text}
            </Button>
          )}
          {secondaryButton && (
            <Button
              fullWidth
              variant="outlined"
              onClick={async () => {
                await onSecondaryClick?.(secondaryButton);
              }}
            >
              {secondaryButton.text}
            </Button>
          )}
        </Stack>
      )}
    </Dialog>
  );
}
